/**
 * Хабы: куда скаут отвозит байки.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * СЕРВЕР (C2) — ЕСЛИ ВЫКАЧЕН, КЛИЕНТСКИЙ СЧЁТ — ЕСЛИ НЕТ
 * ─────────────────────────────────────────────────────────────────────────
 * Контракт C2 добавляет `GET /v1/scout/hubs`, который считает `present`
 * (велики `available` внутри хаба) и `target` (сколько должно стоять) на
 * сервере одним запросом — точнее и без гонки за пагинацией парка. До его
 * выката (или пока `ParkingZone.is_hub`/`target_count` ещё не долетели)
 * считаем то же самое на клиенте: хаб — точечная `allowed`-зона (старое
 * допущение по форме, см. `looksLikeHub`), `present` — велики `available` в
 * радиусе `radiusM` из уже загруженного парка.
 *
 * `target` в клиентском счёте — 0, если `targetCount` ещё не пришёл (старый
 * бэкенд). Ноль — не «хабу не нужны байки» (бизнес-смысла в этом нет), а
 * «неизвестно»; `hubTargetKnown` — явная проверка вместо сравнения с нулём
 * россыпью по компонентам.
 */
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { ApiError, request } from '@/lib/http'
import { distanceM, type LatLng } from '@/lib/geo'
import { displayNumber, useScoutVehicles, type ScoutVehicle } from '@/api/vehicles'

/**
 * `double` с провода: число, строка или `null` — grpc-gateway отдаёт незаданный
 * double как `null` (не `undefined`), на чём `.default(0)` в одиночку падает.
 * Взято из `sharing-admin/src/api/config.ts`, там на этом уже обожглись.
 */
const zDouble0 = z
  .union([z.number(), z.string(), z.null()])
  .default(0)
  .transform((v) => {
    const n = typeof v === 'string' ? Number(v) : (v ?? 0)
    return Number.isFinite(n) ? n : 0
  })

const parkingZoneSchema = z.object({
  id: z.string().default(''),
  name: z.string().default(''),
  kind: z.string().default(''),
  centerLat: zDouble0,
  centerLng: zDouble0,
  radiusM: zDouble0,
  // C2: явный признак хаба и сколько байков там должно стоять. Пусто/0 у
  // бэкенда, который их ещё не знает, — тогда решает форма (looksLikeHub).
  isHub: z.boolean().default(false),
  targetCount: z.number().default(0),
})
export type ParkingZone = z.infer<typeof parkingZoneSchema>

const parkingZonesReply = z.object({
  zones: z.array(parkingZoneSchema).default([]),
})

export type Hub = {
  id: string
  name: string
  lat: number
  lng: number
  radiusM: number
  /** Велики `available` внутри хаба. */
  present: number
  /** Сколько должно стоять. 0 — неизвестно, см. hubTargetKnown. */
  target: number
  delta: number
  /** Номера байков внутри — для «тап по хабу → список». */
  vehicleNumbers: string[]
}

export function hubTargetKnown(h: Pick<Hub, 'target'>): boolean {
  return h.target > 0
}

/** См. шапку файла: старое допущение по форме, пока `is_hub` не выкачен. */
function looksLikeHub(z: ParkingZone): boolean {
  if (z.isHub) return true
  if (z.kind !== 'allowed') return false
  if (z.radiusM <= 0) return false
  // Ровно (0,0) — это «координат нет», а не точка в Атлантике.
  return z.centerLat !== 0 || z.centerLng !== 0
}

/** Считает present/vehicleNumbers по уже загруженному парку. */
export function computeHubsClientSide(
  zones: readonly ParkingZone[],
  vehicles: readonly ScoutVehicle[],
): Hub[] {
  return zones.filter(looksLikeHub).map((z) => {
    const center: LatLng = { lat: z.centerLat, lng: z.centerLng }
    const inside = vehicles.filter(
      (v) => v.status === 'available' && distanceM(v, center) <= z.radiusM,
    )
    const present = inside.length
    const target = z.targetCount
    return {
      id: z.id,
      name: z.name,
      lat: z.centerLat,
      lng: z.centerLng,
      radiusM: z.radiusM,
      present,
      target,
      delta: target > 0 ? target - present : 0,
      vehicleNumbers: inside.map(displayNumber).filter((n) => n !== ''),
    }
  })
}

/** C2: `GET /v1/scout/hubs`. */
const hubSchema = z.object({
  id: z.string().default(''),
  name: z.string().default(''),
  centerLat: zDouble0,
  centerLng: zDouble0,
  radiusM: zDouble0,
  present: z.number().default(0),
  target: z.number().default(0),
  delta: z.number().default(0),
  vehicleNumbers: z.array(z.string()).default([]),
})
const scoutHubsReply = z.object({ hubs: z.array(hubSchema).default([]) })

function toHub(h: z.infer<typeof hubSchema>): Hub {
  return {
    id: h.id,
    name: h.name,
    lat: h.centerLat,
    lng: h.centerLng,
    radiusM: h.radiusM,
    present: h.present,
    target: h.target,
    delta: h.delta,
    vehicleNumbers: h.vehicleNumbers,
  }
}

/** `null` — ручка ещё не выкачена (404). Другие ошибки не глотаем. */
async function fetchServerHubs(): Promise<Hub[] | null> {
  try {
    const out = await request('/v1/scout/hubs', scoutHubsReply)
    return out.hubs.map(toHub)
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null
    throw e
  }
}

export const HQK = {
  zones: ['scout.hubZones'] as const,
  server: ['scout.hubs.server'] as const,
}

/**
 * Хабы одним списком.
 *
 * Три источника внутри: свой парковочные зоны (детект + клиентский счёт),
 * парк (клиентский счёт `present`) и попытка серверной ручки C2. Карта
 * держит `useScoutVehicles` со своим `refetchInterval` — эта функция его не
 * задаёт сама, но подхватывает то же кэш-значение, потому что ключ запроса
 * один на всё приложение.
 */
export function useHubs(): { data: Hub[] | undefined } {
  const zones = useQuery({
    queryKey: HQK.zones,
    queryFn: async () => (await request('/v1/parking-zones', parkingZonesReply)).zones,
    staleTime: 60 * 60_000,
  })
  const vehicles = useScoutVehicles()
  const server = useQuery({
    queryKey: HQK.server,
    queryFn: fetchServerHubs,
    staleTime: 60_000,
    retry: false,
  })

  const data = useMemo(() => {
    if (zones.data === undefined) return undefined
    if (server.data) return server.data
    return computeHubsClientSide(zones.data, vehicles.data?.vehicles ?? [])
  }, [zones.data, vehicles.data, server.data])

  return { data }
}

/** Ближайший хаб к точке. `null`, когда неизвестно, где мы, или хабов нет. */
export function nearestHub(
  from: LatLng | null,
  hubs: readonly Hub[] | undefined,
): { hub: Hub; distance: number } | null {
  if (from === null) return null
  if (hubs === undefined || hubs.length === 0) return null

  let best: { hub: Hub; distance: number } | null = null
  for (const hub of hubs) {
    const distance = distanceM(from, hub)
    if (best === null || distance < best.distance) best = { hub, distance }
  }
  return best
}

/** Хаб по id — для `ScoutTask.targetZoneId`. `null`, если такого нет. */
export function hubById(id: string, hubs: readonly Hub[] | undefined): Hub | null {
  if (id === '') return null
  return hubs?.find((h) => h.id === id) ?? null
}
